import { useEffect, useRef, useId } from 'react';

/**
 * AdSlot
 * -------
 * Drop-in ad unit for interspersing Google ads inside a results list.
 * Supports both AdSense (default) and Google Ad Manager (GAM) via the
 * `provider` prop, so you can start with AdSense and switch later
 * without changing how you call this component elsewhere.
 *
 * IMPORTANT: each rendered instance needs a UNIQUE `id` — reusing one
 * across a paginated/infinite-scroll list is the #1 cause of
 * "adsbygoogle already have ads in it" / duplicate GAM slot errors.
 *
 * ENV-BASED CONFIG (webpack)
 * -----------------
 * You normally won't pass `clientId` / `gamAdUnitPath` by hand — set the
 * env vars below (via .env files read by dotenv-webpack / DefinePlugin,
 * whichever your webpack config uses) and the component resolves them
 * automatically. This is what makes staging vs. production swap without
 * touching component-call sites:
 *
 *   REACT_APP_ADSENSE_CLIENT_ID=ca-pub-XXXXXXXXXXXXXXXX
 *   REACT_APP_GAM_NETWORK_CODE=1234567
 *   REACT_APP_ADS_TEST_MODE=true   -> forces Google's public demo ad unit
 *                                     instead of your real network, regardless
 *                                     of NODE_ENV. Leave unset in production.
 *
 * Note: if your webpack config doesn't already expose `process.env.*` to
 * the browser bundle (CRA does this by default; a hand-rolled webpack
 * config needs `webpack.DefinePlugin` or the `dotenv-webpack` plugin),
 * add that first or these will always read as undefined at runtime.
 *
 * If REACT_APP_ADS_TEST_MODE isn't set, test mode is inferred from
 * NODE_ENV !== 'production' — so local dev and most staging builds get
 * safe test ads automatically, and only a real production build serves
 * real ads.
 */

// --- Env resolution (webpack: process.env.* must be exposed to the bundle) -

function readEnv(key) {
	if (typeof process !== 'undefined' && process.env?.[key]) {
		return process.env[key];
	}
	return undefined;
}

function readIsDev() {
	if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
		return process.env.NODE_ENV !== 'production';
	}
	return true; // safest default: treat unknown environments as test mode
}

const GOOGLE_DEMO_AD_UNIT = '/6355419/Travel/Europe/France/Paris';
const GOOGLE_DEMO_SIZE = [[300, 250]];

const AD_ENV = {
	adsenseClientId: readEnv('REACT_APP_ADSENSE_CLIENT_ID'),
	gamNetworkCode: readEnv('REACT_APP_GAM_NETWORK_CODE'),
	forcedTestMode: readEnv('REACT_APP_ADS_TEST_MODE'),
	isDev: readIsDev(),
};

const IS_TEST_MODE =
	AD_ENV.forcedTestMode !== undefined ? AD_ENV.forcedTestMode === 'true' : AD_ENV.isDev;

/**
 * Resolves a GAM ad unit path from a short placement name plus your
 * configured network code, e.g. resolveGamPath('search_results_infeed')
 * -> '/1234567/search_results_infeed'. In test mode this is ignored in
 * favor of Google's public demo unit, so you never need real credentials
 * to develop locally.
 */
function resolveGamPath(placement) {
	if (IS_TEST_MODE) return GOOGLE_DEMO_AD_UNIT;
	if (!AD_ENV.gamNetworkCode) return undefined;
	return `/${AD_ENV.gamNetworkCode}/${placement}`;
}

// --- One-time script loaders -------------------------------------------

let adsenseLoaded = false;
function loadAdSenseScript(clientId) {
	if (adsenseLoaded) return;
	adsenseLoaded = true;
	const script = document.createElement('script');
	script.async = true;
	script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
	script.crossOrigin = 'anonymous';
	document.head.appendChild(script);
}

let gamLoaded = false;
function loadGamScript() {
	if (gamLoaded) return;
	gamLoaded = true;
	const script = document.createElement('script');
	script.async = true;
	script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
	document.head.appendChild(script);
	window.googletag = window.googletag || { cmd: [] };
}

// --- Component -----------------------------------------------------------

export default function AdSlot({
																 id, // unique id, e.g. `ad-${index}` — required
																 provider = 'adsense', // 'adsense' | 'gam'
																 placement, // short name, e.g. 'search_results_infeed' — preferred over gamAdUnitPath
																 // AdSense config (falls back to REACT_APP_ADSENSE_CLIENT_ID)
																 clientId = AD_ENV.adsenseClientId,
																 slotId, // AdSense ad slot id
																 format = 'fluid', // 'fluid' works well for in-feed native ads
																 layoutKey, // required by AdSense for in-feed/in-article units
																 // GAM config — pass `placement` instead of this in normal use
																 gamAdUnitPath = placement ? resolveGamPath(placement) : undefined,
																 gamSize = IS_TEST_MODE ? GOOGLE_DEMO_SIZE : [[300, 250], [336, 280]],
																 targeting = {}, // e.g. { category: 'horses', locale: 'de' }
																 // shared
																 minHeight = 250, // reserve height to avoid layout shift (CLS)
																 label = IS_TEST_MODE ? 'Advertisement (test)' : 'Advertisement',
															 }) {
	const containerRef = useRef(null);
	const reactId = useId();
	const uniqueId = id || `ad-slot-${reactId}`;

	useEffect(() => {
		if (provider === 'adsense') {
			if (!clientId) {
				console.warn('AdSlot: missing clientId for AdSense');
				return;
			}
			loadAdSenseScript(clientId);
			try {
				(window.adsbygoogle = window.adsbygoogle || []).push({});
			} catch (e) {
				console.error('AdSense push failed', e);
			}
		}

		if (provider === 'gam') {
			if (!gamAdUnitPath) {
				console.warn('AdSlot: missing gamAdUnitPath for Ad Manager');
				return;
			}
			loadGamScript();
			window.googletag.cmd.push(() => {
				const slot = window.googletag
					.defineSlot(gamAdUnitPath, gamSize, uniqueId)
					?.addService(window.googletag.pubads());

				if (slot) {
					Object.entries(targeting).forEach(([key, value]) => {
						slot.setTargeting(key, String(value));
					});
				}

				window.googletag.pubads().enableSingleRequest();
				window.googletag.enableServices();
				window.googletag.display(uniqueId);
			});

			// Clean up the slot on unmount (important for infinite-scroll lists)
			return () => {
				window.googletag?.cmd.push(() => {
					const slots = window.googletag.pubads().getSlots();
					const mine = slots.find((s) => s.getSlotElementId() === uniqueId);
					if (mine) window.googletag.destroySlots([mine]);
				});
			};
		}
	}, [provider, clientId, gamAdUnitPath, uniqueId]);

	return (
		<div
			className="w-full flex flex-col items-center justify-center bg-gray-50 border border-gray-100 rounded-lg my-2"
			style={{ minHeight }}
		>
      <span className="text-[10px] uppercase tracking-wide text-gray-400 mb-1 mt-2">
        {label}
      </span>

			{provider === 'adsense' && (
				<ins
					ref={containerRef}
					className="adsbygoogle"
					style={{ display: 'block', width: '100%' }}
					data-ad-client={clientId}
					data-ad-slot={slotId}
					data-ad-format={format}
					data-ad-layout-key={layoutKey}
				/>
			)}

			{provider === 'gam' && (
				<div id={uniqueId} ref={containerRef} style={{ width: '100%' }} />
			)}
		</div>
	);
}